/**
 * The real identity of a payhead row is category_id
 * (pms_salary_categories._Id) - category_name alone is ambiguous when
 * two categories share a name across sections (e.g. "Provident Fund"
 * under both Employer and Employee Deduction), so anything keying a
 * lookup/map on a row must use this, never category_name directly. Falls
 * back to category_name only for a row saved before category_id existed
 * on this table.
 */
export const rowKey = (row) => (row.category_id !== undefined && row.category_id !== null ? row.category_id : row.category_name);

// Mirrors Kasturi-Node-Backend/Config/constants.js's
// EARNING_PARENT_CATEGORY_IDS / DEDUCTION_PARENT_CATEGORY_IDS.
const EARNING_PARENT_CATEGORY_IDS = [1, 2];
export const DEDUCTION_PARENT_CATEGORY_IDS = [3, 4];

const categoryInfo = (row, categories) => {
  if (row.category_id != null) {
    const match = categories.find((c) => c.id === row.category_id);
    if (match) return match;
  }
  return categories.find((c) => c.category_name === row.category_name) || {};
};

/**
 * Client-side preview only - mirrors the server's resolveWageRateComponents
 * (Kasturi-Node-Backend/Services/payroll/wageRateComponents.js) just
 * closely enough to show a live total as the user types. The server
 * always recomputes and is the actual source of truth.
 *
 * baseRateAmount is the FIXED reference figure ("% of Rate" resolves
 * against this same number for every row, not a running sum) - pass
 * undefined when there's no concrete rate yet (e.g. editing a reusable
 * template, which isn't tied to one employee's rate) and any "% of Rate"
 * row will just preview as 0 until it's actually applied to a rate.
 *
 * categories: the active salary-category list (each with id/category_name/
 * parent_category_id), needed to know which rows are "earnings" for
 * "% of Gross" (sum of Standard Monthly Salary + Extra Earnings) - pass
 * [] where nothing uses a Gross base.
 *
 * Returns grossTotal (earnings-only sum - what must equal Rate Amount)
 * separately from total (every row summed, informational only, since
 * deduction rows are never actually subtracted here - see
 * PayrollCalculationService for where the real sign-flip happens).
 */
export const resolvePayheadAmounts = (rows, baseRateAmount, categories = []) => {
  const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  let basicAmount = null;
  let grossTotal = 0;
  let total = 0;
  const amountByCategory = {};
  const deferred = [];

  const resolveOne = (row, base) => {
    const value = Number(row.cal_value) || 0;
    const amount = row.cal_method === "PERCENTAGE" ? Math.round(((base * value) / 100) * 100) / 100 : Math.round(value * 100) / 100;

    const info = categoryInfo(row, categories);
    if (EARNING_PARENT_CATEGORY_IDS.includes(Number(info.parent_category_id))) {
      grossTotal = Math.round((grossTotal + amount) * 100) / 100;
    }

    total = Math.round((total + amount) * 100) / 100;
    amountByCategory[rowKey(row)] = amount;
    return amount;
  };

  // Basic is resolved first, independent of sort_order - it can only be
  // Fixed or a % of Rate (never of itself, and never of Gross), both
  // fully known up front, so whichever payhead is flagged Basic works
  // correctly no matter where it sorts relative to the rows that
  // reference it.
  const basicRow = sorted.find((r) => r.is_basic);
  if (basicRow) {
    basicAmount = resolveOne(basicRow, Number(baseRateAmount) || 0);
  }

  for (const row of sorted) {
    if (row === basicRow) continue;

    if (row.cal_method === "PERCENTAGE" && row.cal_from === "GROSS") {
      deferred.push(row);
      continue;
    }

    const base = row.cal_from === "BASIC" ? basicAmount || 0 : Number(baseRateAmount) || 0;
    resolveOne(row, base);
  }

  for (const row of deferred) {
    const value = Number(row.cal_value) || 0;
    const amount = grossTotal ? Math.round(((grossTotal * value) / 100) * 100) / 100 : 0;
    total = Math.round((total + amount) * 100) / 100;
    amountByCategory[rowKey(row)] = amount;
  }

  return { amountByCategory, total, grossTotal };
};

export const previewPayheadTotal = (rows, baseRateAmount, categories = []) => resolvePayheadAmounts(rows, baseRateAmount, categories).total;

/** The figure that must equal Rate Amount - earnings only, deductions excluded. */
export const previewGrossTotal = (rows, baseRateAmount, categories = []) =>
  resolvePayheadAmounts(rows, baseRateAmount, categories).grossTotal;

const isBlank = (val) => val === "" || val === null || val === undefined;

/**
 * Same structural rules as the backend's validateWageRateComponents.
 * Whichever payhead is flagged Basic resolves correctly regardless of
 * its position in the list (see resolvePayheadAmounts) - the only
 * requirement is that SOME row is flagged Basic when another row uses
 * "% of Basic". categories is optional - pass it to also enforce "% of
 * Gross is deduction-only"; omit it to skip just that one check.
 */
export const validatePayheadRows = (rows, categories = []) => {
  const hasBasic = rows.some((r) => r.is_basic);

  for (const [index, row] of rows.entries()) {
    if (!row.category_name?.trim()) return `Component ${index + 1}: name is required`;
    if (isBlank(row.cal_value) || Number(row.cal_value) <= 0) {
      return `${row.category_name}: value must be greater than 0`;
    }
    if (row.is_basic && row.cal_method === "PERCENTAGE" && row.cal_from === "BASIC") {
      return `${row.category_name}: is flagged as Basic, so it can't be a percentage of itself`;
    }
    if (row.is_basic && row.cal_method === "PERCENTAGE" && row.cal_from === "GROSS") {
      return `${row.category_name}: is flagged as Basic, so it can't be a percentage of Gross - use % of Rate instead`;
    }
    if (row.cal_method === "PERCENTAGE" && row.cal_from === "BASIC" && !hasBasic) {
      return `${row.category_name}: is a % of Basic, but no component is flagged as Basic`;
    }
    if (row.cal_method === "PERCENTAGE" && row.cal_from === "GROSS" && categories.length > 0) {
      const info = categoryInfo(row, categories);
      if (!DEDUCTION_PARENT_CATEGORY_IDS.includes(Number(info.parent_category_id))) {
        return `${row.category_name}: is a % of Gross, but that's only available for an Employer/Employee Deduction payhead`;
      }
    }
  }

  return null;
};

/**
 * Structural check + (when rateAmount is given) the CTC-style
 * Components-must-equal-Rate-Amount check (ALL four sections - Standard
 * Monthly Salary, Extra Earnings, Employer Deduction, Employee Deduction
 * - summed together, e.g. Rate 100 = Earnings 75 + Deductions 25),
 * combined - this is what AddEditWageRate.jsx recomputes on every render
 * (not just on Submit) so the error appears/updates/clears live as the
 * admin edits a value, instead of sitting stale from the last Submit
 * attempt. rateAmount undefined (e.g. the Payroll Template form, which
 * has no concrete rate) skips the mismatch half entirely.
 *
 * includeMismatch: false skips the Rate Amount comparison and returns
 * only structural problems - PayheadRowsEditor already shows its own
 * live "Components total... must match Rate Amount" line, so a caller
 * rendering that same error text elsewhere would just duplicate it;
 * pass false there and keep the default (true) for the actual
 * Submit-blocking check, which still needs to catch a mismatch.
 */
export const computePayheadsError = (rows, rateAmount, categories = [], { includeMismatch = true } = {}) => {
  if (rows.length === 0) return null;

  const structuralError = validatePayheadRows(rows, categories);
  if (structuralError) return structuralError;

  if (!includeMismatch || rateAmount === undefined || isBlank(rateAmount) || Number.isNaN(Number(rateAmount))) {
    return null;
  }

  const total = previewPayheadTotal(rows, Number(rateAmount), categories);
  if (Math.abs(total - Number(rateAmount)) > 0.01) {
    return `Components total ₹${total.toFixed(2)} doesn't match Rate Amount ₹${Number(rateAmount).toFixed(
      2
    )} - fix the breakdown or the rate before saving.`;
  }

  return null;
};

export const toPayheadPayload = (rows) =>
  rows.map((row, index) => ({
    category_name: row.category_name,
    category_id: row.category_id ?? null,
    cal_method: row.cal_method,
    cal_value: Number(row.cal_value),
    cal_from: row.cal_method === "PERCENTAGE" ? row.cal_from : null,
    is_basic: Boolean(row.is_basic),
    // Preserve the payhead's position in the full category list (set when
    // the row was created in PayheadRowsEditor) purely for stable display
    // order - resolution no longer depends on it (Basic always resolves
    // first regardless of position; see resolveWageRateComponents).
    sort_order: row.sort_order ?? index,
  }));
